import { useState } from 'react';
import { escapeString } from '../../shared';
import { setupSafelyCallItemToID } from './shared';
export const useDetermineFilteredListItems = (useCheckboxSelection) => {
    const [listItems, setListItems] = useState([]);
    const [filteredAnchoredItemsCount, setFilteredAnchoredItemsCount] = useState(0);
    /**
     * Filter items based on input value
     * @param filterParams - object of items needed to filter correctly
     * @param inputText - value from the `input` element
     * @param currentlySelected - `selectedItem(s)` set by Downshift
     */
    const updateFilteredListboxItems = (filterParams, inputText, currentlySelected) => {
        const { itemIsAnchored = () => false, items = [], itemToID, renderItemToString } = filterParams;
        const isSelectedAnArray = Array.isArray(currentlySelected);
        const inputValueRegex = new RegExp(escapeString(inputText), 'i');
        const anchoredItems = [];
        const nonAnchoredItems = [];
        const safelyCallItemToID = (listboxItem) => {
            return setupSafelyCallItemToID(itemToID, renderItemToString, listboxItem);
        };
        const customCreatedItems = [];
        if (isSelectedAnArray && currentlySelected.length && typeof currentlySelected[0] === 'object') {
            // @ts-expect-error - Already mixing types with AllowCreateProps, we need to come back to fix - TS-31874
            customCreatedItems.push(...currentlySelected.filter((item) => item?.isCreateable));
        }
        if (customCreatedItems.length && useCheckboxSelection) {
            anchoredItems.push(...customCreatedItems);
        }
        // Filtered items from `items`
        const filteredItems = items.filter((item) => !inputText || renderItemToString(item).match(inputValueRegex));
        const filteredItemsNotSelected = filteredItems.filter((item) => {
            if (isSelectedAnArray) {
                const isItemSelected = currentlySelected.filter((selected) => safelyCallItemToID(item) === safelyCallItemToID(selected));
                return isItemSelected.length === 0 && item;
            }
            else {
                return currentlySelected ? safelyCallItemToID(item) !== safelyCallItemToID(currentlySelected) : item;
            }
        });
        // DO NOT remove selected items from the filtered list if using checkbox selection
        if (useCheckboxSelection) {
            // From all items: separate anchored and non-anchored items
            filteredItems.forEach((filteredItem) => {
                const filteredItemIsAnchored = itemIsAnchored(filteredItem);
                if (filteredItemIsAnchored) {
                    anchoredItems.push(filteredItem);
                }
                else {
                    nonAnchoredItems.push(filteredItem);
                }
            });
        }
        else {
            // From non-selected items: separate anchored and non-anchored items
            filteredItemsNotSelected.forEach((filteredItem) => {
                const filteredItemIsAnchored = itemIsAnchored(filteredItem);
                if (filteredItemIsAnchored) {
                    anchoredItems.push(filteredItem);
                }
                else {
                    nonAnchoredItems.push(filteredItem);
                }
            });
        }
        // Always set filtered anchored items, including selected
        // item if it's not an array and not using checkbox selection
        const updatedAnchoredItems = currentlySelected && !isSelectedAnArray && !useCheckboxSelection
            ? [currentlySelected, ...anchoredItems]
            : anchoredItems;
        /**
         * Update with filtered items based on input value,
         * combing filtered anchored items (which can dynamically
         * include selected items), and filtered non-anchored items.
         */
        setFilteredAnchoredItemsCount(updatedAnchoredItems.length);
        // @ts-expect-error - Already mixing types with AllowCreateProps, we need to come back to fix - TS-31874
        setListItems([...updatedAnchoredItems, ...nonAnchoredItems]);
    };
    return {
        filteredAnchoredItemsCount,
        listItems,
        setListItems,
        updateFilteredListboxItems,
    };
};
